// Componente per sistema ExeDialog DDT2026 - Step targa
// Autocomplete che chiama verificaDdt ad ogni carattere digitato
import React, { useState, useRef, useEffect } from 'react';
import { AutoComplete } from 'primereact/autocomplete';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ValoriVerificati } from './ValoriVerificati';
import { WebCam } from './WebCam';

export function Targa(props) {
    const { currURLSearchParams, onFinish, verificaData, verificaDdt, buildInputFromSearchParams } = props;
    const inputRef = useRef();
    
    const [state, setState] = useState({
        targa: currURLSearchParams.get('targa') || ''
    });
    const [suggestions, setSuggestions] = useState([]);
    const [searching, setSearching] = useState(false);

    // righe_suggerimento dalla risposta iniziale (targhe recenti)
    const suggerimenti = verificaData?.righe_suggerimento || [];

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [verificaData]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (state.targa) {
            onFinish({ targa: state.targa });
        }
    };

    // Selezionando una riga, precompila vettore, conducente, cig, cup (solo valori di input, non codici derivati)
    const handleRowSelect = (e) => {
        const row = e.data;
        const values = {
            targa: row.targa,
        };
        // Aggiungi campi opzionali se presenti nella riga (solo valori di input, non codici come cod_vettore, conducente)
        if (row.rag_soc_vettore) values.rag_soc_vettore = row.rag_soc_vettore;
        if (row.vett_ns !== undefined) values.vett_ns = row.vett_ns;
        if (row.rag_soc_conducente) values.rag_soc_conducente = row.rag_soc_conducente;
        if (row.cig) values.cig = row.cig;
        if (row.cup) values.cup = row.cup;
        if (row.tara) values.tara_veicolo = row.tara;
        
        onFinish(values);
    };

    // Chiama verificaDdt ad ogni carattere digitato
    const searchOptions = async (event) => {
        const query = event.query?.toUpperCase();
        if (!query || query.length < 1) {
            setSuggestions([]);
            return;
        }

        setSearching(true);
        try {
            const currentInput = buildInputFromSearchParams(currURLSearchParams);
            const input = {
                ...currentInput,
                targa: query
            };
            
            const result = await verificaDdt(input);
            // Se il campo è stato verificato (campo_da_verificare è passato al successivo),
            // mostra il valore verificato come unica opzione confermabile
            if (result?.campo_da_verificare !== 'targa') {
                const v = result?.valori_verificati;
                setSuggestions(v?.targa ? [{ targa: v.targa, tara: result?.valori_accessori?.tara_veicolo }] : []);
            } else {
                setSuggestions(result?.opzioni_selezione || []);
            }
        } catch (err) {
            console.error('Errore ricerca:', err);
            setSuggestions([]);
        } finally {
            setSearching(false);
        }
    };

    // Template per mostrare targa nell'autocomplete
    const itemTemplate = (item) => (
        <span>{item.targa} <span style={{fontSize: 'x-small', color: '#666'}}>{item.tara ? `(tara: ${item.tara})` : ''}</span></span>
    );

    // Calcola "trasporto a nostro carico" da mittente, mezzo_vettore e vett_ns
    const trasportoCaricaNostroBody = (row) => {
        const trasportoNostro = row.mittente || (row.mezzo_vettore && row.vett_ns);
        return trasportoNostro ? 'Sì' : 'No';
    };

    return (
        <div className="card-body">
            <h5 className="card-title">Targa del Veicolo</h5>
            <p className="text-muted">Digita per cercare o seleziona dalla tabella delle targhe recenti.</p>
            
            <form onSubmit={handleSubmit}>
                <div className="mb-3">
                    <label htmlFor="targa" className="form-label">Targa</label>
                    <AutoComplete 
                        ref={inputRef}
                        id="targa"
                        value={state.targa}
                        suggestions={suggestions}
                        completeMethod={searchOptions}
                        field="targa"
                        onChange={(e) => {
                            const value = typeof e.value === 'object' ? e.value.targa : e.value;
                            setState({ targa: value?.toUpperCase() || '' });
                        }}
                        onSelect={(e) => onFinish({ targa: e.value.targa })}
                        itemTemplate={itemTemplate}
                        className="w-100"
                        inputClassName="w-100"
                        style={{ textTransform: 'uppercase' }}
                        minLength={1}
                        delay={300}
                    />
                    {searching && <small className="text-muted">Ricerca in corso...</small>}
                </div>

                {suggerimenti.length > 0 && (
                    <>
                    <div className="card mb-3">
                        <DataTable 
                            value={suggerimenti} 
                            selectionMode="single" 
                            onRowSelect={handleRowSelect}
                            size="small"
                            scrollable
                            scrollHeight="500px"
                            header="Veicoli e conducenti recenti"
                            filterDisplay="row"
                        >
                            <Column field="targa" header="Targa" sortable filter showFilterMenu={false} />
                            <Column field="tara" header="Tara" sortable />
                            <Column field="rag_soc_conducente" header="Conducente" sortable />
                            <Column field="rag_soc_vettore" header="Vettore" sortable />
                            <Column header="Trasp. nostro" body={trasportoCaricaNostroBody} sortable />
                            <Column field="cig" header="CIG" sortable />
                            <Column field="cup" header="CUP" sortable />
                        </DataTable>
                    </div>
                    <small className="text-muted mb-3 d-block">Selezionando una riga verranno precompilati vettore, conducente, CIG e CUP.</small>
                    </>
                )}

                <button className="btn btn-primary" disabled={!state.targa}>
                    Prosegui
                </button>
            </form>
            
            <WebCam verificaData={verificaData} target="targa" />
            
            <ValoriVerificati valori={verificaData?.valori_verificati} />
        </div>
    );
}

Targa.displayName = 'targa';
